"use client";

import React from "react";
import { CircleCheck, Loader, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = ["Flexible Hours", "Weekly Payouts", "Premium Client Base"];
function DriverForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-fade-in animate-slide-up">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4 animate-zoom-in">
          <CircleCheck className="size-8 text-primary transition-transform duration-500 scale-100" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Application Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Application
        </Button>
      </div>;
  }
  const inputClasses = "flex h-12 w-full rounded-md border border-border bg-surface px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:shadow-md disabled:cursor-not-allowed disabled:opacity-50 transition-[border-color,box-shadow,ring] duration-300";
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="full_name" rules={{
          required: "Full Name is required"
        }} render={({
          field
        }) => <FormItem className="min-w-0">
                <FormLabel className="text-foreground/80 font-medium">Full Name</FormLabel>
                <FormControl>
                  <input type="text" className={inputClasses} placeholder="Jane Doe" {...field} />
                </FormControl>
                <p className="text-[10px] text-muted-foreground mt-1">Enter your legal name as it appears on your driver's license.</p>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem className="min-w-0">
                <FormLabel className="text-foreground/80 font-medium">Email Address</FormLabel>
                <FormControl>
                  <input type="email" className={inputClasses} placeholder="jane@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^\+?[0-9\s\-\(\)]+$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem className="min-w-0">
                <FormLabel className="text-foreground/80 font-medium">Phone Number</FormLabel>
                <FormControl>
                  <input type="tel" className={inputClasses} placeholder="+1 555-123-4567" {...field} />
                </FormControl>
                <p className="text-[10px] text-muted-foreground mt-1">We'll use this to contact you about your application.</p>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="city" rules={{
          required: "City is required"
        }} render={({
          field
        }) => <FormItem className="min-w-0">
                <FormLabel className="text-foreground/80 font-medium">City</FormLabel>
                <FormControl>
                  <input type="text" className={inputClasses} placeholder="New York" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="vehicle_type" rules={{
          required: "Vehicle type is required"
        }} render={({
          field
        }) => <FormItem className="min-w-0">
                <FormLabel className="text-foreground/80 font-medium">Vehicle Type</FormLabel>
                <FormControl>
                  <select className={inputClasses} {...field}>
                    <option value="" disabled>Select your vehicle type</option>
                    <option value="Sedan">Sedan</option>
                    <option value="SUV">SUV</option>
                    <option value="Van">Van</option>
                    <option value="Luxury">Luxury</option>
                    <option value="Electric">Electric</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="license_number" rules={{
          required: "License number is required"
        }} render={({
          field
        }) => <FormItem className="min-w-0">
                <FormLabel className="text-foreground/80 font-medium">Driver License Number</FormLabel>
                <FormControl>
                  <input type="text" className={inputClasses} placeholder="D1234567" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="license_file" rules={{
        required: "License upload is required"
      }} render={({
        field: {
          value,
          onChange,
          ...field
        }
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Upload Driver License</FormLabel>
              <FormControl>
                <div className="flex items-center gap-4">
                  <label className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-surface px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
                    <Upload className="size-4" />
                    <span>{value ? "File selected" : "Choose file"}</span>
                    <input type="file" className="hidden" onChange={e => onChange(e.target.files?.[0] || null)} {...field} />
                  </label>
                </div>
              </FormControl>
              <p className="text-[10px] text-muted-foreground mt-1">Provide a clear photo or scan of the front side of your license.</p>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="motivation" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Why do you want to drive with distinction?</FormLabel>
              <FormControl>
                <textarea className={`${inputClasses} min-h-[100px] resize-y py-3`} placeholder="Tell us what sets you apart as a driver..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-3 text-sm bg-destructive/10 text-destructive rounded-md border border-destructive/20">
            {submitError}
          </div>}

        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full mt-6 text-base font-semibold shadow-lg h-12">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting Application...
            </> : 'Join the Fleet'}
        </Button>
      </div>
    </Form>;
}
export default function DriveWithDistinction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="drive-with-distinction" className="relative min-h-screen flex items-center py-24 lg:py-32 bg-background overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/smiling_professional_driver.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/smiling_professional_driver.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/smiling_professional_driver.png-wvc-sizes" className="w-full h-full object-cover object-center opacity-15 blur-[2px]" alt="Smiling professional driver" loading="lazy" />
        <div className="absolute inset-0 bg-white dark:bg-black/80 backdrop-blur-[1px]"></div>
      </div>

      <div ref={ref} className={`container relative z-10 mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid lg:grid-cols-12 gap-6 md:gap-8 lg:gap-16 items-center">
          
          <div className="lg:col-span-5 min-w-0 flex flex-col justify-center space-y-8">
            <h2 className={`font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground tracking-tight ${isVisible ? "animate-fade-in animate-slide-up [animation-duration:700ms]" : "opacity-0"}`}>
              "Moveonix gave me the freedom to earn with dignity."
            </h2>
            
            <div className="space-y-6 pt-4">
              <ul className="space-y-5">
                {benefits.map((benefit, i) => <li key={i} data-index={i} className={`flex items-center gap-4 text-lg md:text-xl font-medium text-foreground/90 ${isVisible ? "animate-fade-in animate-slide-up" : "opacity-0"}`} style={{
                animationDelay: `${(i + 1) * 100}ms`
              }}>
                    <div className="flex-shrink-0 size-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <CircleCheck className="text-primary size-5" />
                    </div>
                    {benefit}
                  </li>)}
              </ul>
            </div>
          </div>

          <div className={`lg:col-span-7 lg:pl-6 xl:pl-8 ${isVisible ? "animate-fade-in animate-slide-up" : "opacity-0"}`}>
            <Card className="shadow-2xl border-border/40 bg-white dark:bg-card/95 backdrop-blur-xl">
              <CardHeader className="pb-4 border-b border-border/50 mb-4">
                <CardTitle className="text-2xl md:text-3xl font-default text-foreground">Drive with Distinction</CardTitle>
                <CardDescription className="text-base text-muted-foreground">
                  Driver Sign-Up. Join our exclusive fleet of premium professionals.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="drive_with_distinction_form_home" sectionName="DriveWithDistinction" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                city: "",
                vehicle_type: "",
                license_number: "",
                license_file: null,
                motivation: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                city: {
                  required: true
                },
                vehicle_type: {
                  required: true
                },
                license_number: {
                  required: true
                },
                license_file: {
                  required: true
                }
              }} submitText="Join the Fleet" successMessage="Thank you for applying to drive with Moveonix. Our team will review your application and contact you shortly.">
                  <DriverForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}